import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Sidebar from '../components/Sidebar';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://metro4.dcairo.com/api';

const Pours = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pours, setPours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    station: 'all',
    testType: 'all',
    dateFrom: '',
    dateTo: ''
  });
  const [showModal, setShowModal] = useState(false);
  const [selectedPour, setSelectedPour] = useState(null);
  const [modalType, setModalType] = useState(''); // 'approve' or 'reject'
  const [notes, setNotes] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  
  const navigate = useNavigate();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // جلب بيانات المستخدم الحالي
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrentUser(response.data.data);
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };
    fetchCurrentUser();
  }, []);

  // جلب الصبات تحت المراجعة
  const fetchPendingPours = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = {
        station: filters.station === 'all' ? undefined : filters.station,
        testType: filters.testType === 'all' ? undefined : filters.testType,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined
      };
      
      const response = await axios.get(`${API_URL}/pours/pending/all`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      if (response.data.success) {
        setPours(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching pending pours:', error);
      if (error.response?.status === 401) {
        toast.error('انتهت الجلسة، يرجى تسجيل الدخول مرة أخرى');
        navigate('/login');
      } else if (error.response?.status === 403) {
        toast.error('ليس لديك صلاحية لعرض الصبات تحت المراجعة');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingPours();
  }, [filters]);

  // فتح مودال الموافقة
  const openApproveModal = (pour) => {
    setSelectedPour(pour);
    setModalType('approve');
    setNotes('');
    setShowModal(true);
  };

  // فتح مودال الرفض
  const openRejectModal = (pour) => {
    setSelectedPour(pour);
    setModalType('reject');
    setNotes('');
    setShowModal(true);
  };

  // إغلاق المودال
  const closeModal = () => {
    setShowModal(false);
    setSelectedPour(null);
    setNotes('');
    setModalType('');
  };

  // الموافقة على الصبة
  const handleApprove = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.patch(
        `${API_URL}/pours/${selectedPour._id}/approve`,
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('تم اعتماد الصبة بنجاح');
        closeModal();
        fetchPendingPours();
      }
    } catch (error) {
      console.error('Error approving pour:', error);
      toast.error(error.response?.data?.message || 'حدث خطأ أثناء الموافقة');
    } finally {
      setLoading(false);
    }
  };

  // رفض الصبة
  const handleReject = async () => {
    if (!notes.trim()) {
      toast.error('سبب الرفض مطلوب');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.patch(
        `${API_URL}/pours/${selectedPour._id}/reject`,
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('تم رفض الصبة');
        closeModal();
        fetchPendingPours();
      }
    } catch (error) {
      console.error('Error rejecting pour:', error);
      toast.error(error.response?.data?.message || 'حدث خطأ أثناء الرفض');
    } finally {
      setLoading(false);
    }
  };

  // تنسيق التاريخ (DD/MM/YYYY)
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB');
  };

  // تنسيق الوقت
  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // الحصول على اسم المحطة
  const getStationName = (pour) => {
    return pour.station || '-';
  };

  // حساب عدد المكعبات
  const calculateCubes = (cubes) => {
    if (!cubes) return '-';
    const { groups, cubesPerGroup } = cubes;
    if (groups && cubesPerGroup) {
      return `${groups}G × ${cubesPerGroup} = ${parseInt(groups) * parseInt(cubesPerGroup)}C`;
    }
    return '-';
  };

  return (
    <div className="dashboard-layout">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <div className="dashboard-content">
        <div className="dashboard-header">
          <button className="menu-toggle" onClick={toggleSidebar}>
            ☰
          </button>
          <h1>📋 سجل الصبات - تحت المراجعة</h1>
        </div>

        <div className="dashboard-main">
          <style>{`
            .pours-container {
              padding: 20px;
              max-width: 100%;
              margin: 0 auto;
            }

            .pours-header {
              background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
              padding: 30px;
              border-radius: 20px;
              box-shadow: 0 8px 30px rgba(0,0,0,0.08);
              margin-bottom: 30px;
              border-top: 6px solid #f59e0b;
              position: relative;
              overflow: hidden;
            }

            .pours-header::before {
              content: '';
              position: absolute;
              top: 0;
              right: 0;
              width: 200px;
              height: 200px;
              background: linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, transparent 100%);
              border-radius: 50%;
              transform: translate(30%, -30%);
            }

            .header-content {
              position: relative;
              z-index: 1;
            }

            .header-title {
              font-size: 28px;
              font-weight: 900;
              color: #1e293b;
              margin: 0 0 15px 0;
              display: flex;
              align-items: center;
              gap: 15px;
              letter-spacing: -0.5px;
            }

            .header-icon {
              font-size: 36px;
              filter: drop-shadow(0 2px 4px rgba(245, 158, 11, 0.3));
            }

            .header-subtitle {
              font-size: 15px;
              color: #64748b;
              margin: 0 0 25px 0;
              font-weight: 500;
            }

            .filters-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
              gap: 18px;
            }

            .filter-group {
              display: flex;
              flex-direction: column;
              gap: 10px;
            }

            .filter-label {
              font-size: 14px;
              font-weight: 700;
              color: #475569;
              display: flex;
              align-items: center;
              gap: 6px;
            }

            .filter-icon {
              font-size: 16px;
            }

            .filter-select,
            .filter-input {
              padding: 12px 16px;
              border: 2px solid #e2e8f0;
              border-radius: 12px;
              font-size: 14px;
              transition: all 0.3s;
              background: white;
              font-weight: 500;
            }

            .filter-select:focus,
            .filter-input:focus {
              outline: none;
              border-color: #f59e0b;
              box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.1);
              transform: translateY(-2px);
            }

            .filter-select:hover,
            .filter-input:hover {
              border-color: #cbd5e1;
            }

            /* DatePicker Styles */
            .date-picker-wrapper {
              position: relative;
            }

            .date-picker-wrapper .react-datepicker-wrapper {
              width: 100%;
            }

            .date-picker-wrapper .react-datepicker__input-container input {
              width: 100%;
              padding: 12px 16px;
              border: 2px solid #e2e8f0;
              border-radius: 12px;
              font-size: 14px;
              transition: all 0.3s;
              background: white;
              font-weight: 500;
              box-sizing: border-box;
            }

            .date-picker-wrapper .react-datepicker__input-container input:focus {
              outline: none;
              border-color: #f59e0b;
              box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.1);
            }

            .date-picker-wrapper .react-datepicker__input-container input:hover {
              border-color: #cbd5e1;
            }

            .react-datepicker {
              font-family: inherit;
              border: 2px solid #e2e8f0;
              border-radius: 12px;
              box-shadow: 0 10px 40px rgba(0,0,0,0.15);
            }

            .react-datepicker__header {
              background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
              border-bottom: none;
              border-radius: 10px 10px 0 0;
              padding: 15px;
            }

            .react-datepicker__current-month {
              color: white;
              font-weight: 700;
              font-size: 16px;
            }

            .react-datepicker__day-name {
              color: rgba(255,255,255,0.9);
              font-weight: 600;
            }

            .react-datepicker__day--selected {
              background: #f59e0b;
              border-radius: 8px;
            }

            .react-datepicker__day:hover {
              background: #fef3c7;
              border-radius: 8px;
            }

            .react-datepicker__navigation-icon::before {
              border-color: white;
            }

            .stats-card {
              background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
              padding: 25px;
              border-radius: 16px;
              box-shadow: 0 8px 25px rgba(245, 158, 11, 0.3);
              margin-bottom: 30px;
              color: white;
              display: flex;
              align-items: center;
              justify-content: space-between;
            }

            .stat-content {
              flex: 1;
            }

            .stat-value {
              font-size: 48px;
              font-weight: 900;
              margin: 0 0 8px 0;
              text-shadow: 0 2px 4px rgba(0,0,0,0.2);
            }

            .stat-label {
              font-size: 16px;
              font-weight: 600;
              margin: 0;
              opacity: 0.95;
            }

            .stat-icon {
              font-size: 80px;
              opacity: 0.2;
            }

            .table-card {
              background: white;
              border-radius: 20px;
              box-shadow: 0 8px 30px rgba(0,0,0,0.08);
              overflow: hidden;
              border: 1px solid #f1f5f9;
            }

            .table-header {
              background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
              padding: 20px 25px;
              border-bottom: 3px solid #f59e0b;
            }

            .table-header-title {
              font-size: 20px;
              font-weight: 800;
              color: #1e293b;
              margin: 0;
              display: flex;
              align-items: center;
              gap: 10px;
            }

            .table-wrapper {
              overflow-x: auto;
            }

            .pours-table {
              width: 100%;
              border-collapse: collapse;
            }

            .pours-table thead {
              background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
              color: white;
              position: sticky;
              top: 0;
              z-index: 10;
            }

            .pours-table th {
              padding: 18px 15px;
              text-align: center;
              font-weight: 700;
              font-size: 14px;
              white-space: nowrap;
              text-shadow: 0 1px 2px rgba(0,0,0,0.1);
              letter-spacing: 0.3px;
            }

            .pours-table td {
              padding: 16px 15px;
              border-bottom: 1px solid #f1f5f9;
              font-size: 14px;
              color: #334155;
              font-weight: 500;
              text-align: center;
            }

            .pours-table tbody tr {
              transition: all 0.2s;
              background: white;
            }

            .pours-table tbody tr:nth-child(even) {
              background: #f8fafc;
            }

            .pours-table tbody tr:hover {
              background: #fef3c7;
              transform: scale(1.01);
              box-shadow: 0 4px 12px rgba(245, 158, 11, 0.15);
            }

            .status-badge {
              display: inline-flex;
              align-items: center;
              gap: 6px;
              padding: 8px 16px;
              border-radius: 25px;
              font-size: 13px;
              font-weight: 700;
              background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
              color: #78350f;
              border: 2px solid #f59e0b;
              box-shadow: 0 2px 8px rgba(245, 158, 11, 0.2);
            }

            .test-type-badge {
              display: inline-block;
              padding: 6px 12px;
              border-radius: 8px;
              font-size: 12px;
              font-weight: 700;
              border: 2px solid;
            }

            .test-type-concrete {
              background: #dbeafe;
              color: #1e40af;
              border-color: #3b82f6;
            }

            .test-type-grout {
              background: #fef3c7;
              color: #92400e;
              border-color: #f59e0b;
            }

            .test-type-trail {
              background: #fce7f3;
              color: #9f1239;
              border-color: #ec4899;
            }

            .action-buttons {
              display: flex;
              gap: 8px;
              justify-content: center;
            }

            .btn-approve {
              padding: 8px 16px;
              background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%);
              color: white;
              border: none;
              border-radius: 8px;
              font-size: 13px;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.3s;
              display: flex;
              align-items: center;
              gap: 5px;
            }

            .btn-approve:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(22, 163, 74, 0.3);
            }

            .btn-reject {
              padding: 8px 16px;
              background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
              color: white;
              border: none;
              border-radius: 8px;
              font-size: 13px;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.3s;
              display: flex;
              align-items: center;
              gap: 5px;
            }

            .btn-reject:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
            }

            .btn-approve:disabled,
            .btn-reject:disabled {
              opacity: 0.5;
              cursor: not-allowed;
              transform: none;
            }

            .loading-container {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 80px 20px;
            }

            .loading-spinner {
              width: 60px;
              height: 60px;
              border: 5px solid #f1f5f9;
              border-top: 5px solid #f59e0b;
              border-radius: 50%;
              animation: spin 1s linear infinite;
              margin-bottom: 20px;
            }

            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }

            .loading-text {
              color: #64748b;
              font-size: 16px;
              font-weight: 600;
            }

            .empty-state {
              text-align: center;
              padding: 80px 20px;
            }

            .empty-icon {
              font-size: 80px;
              margin-bottom: 20px;
              opacity: 0.5;
            }

            .empty-title {
              font-size: 24px;
              font-weight: 700;
              color: #1e293b;
              margin: 0 0 10px 0;
            }

            .empty-text {
              font-size: 16px;
              color: #64748b;
              margin: 0;
            }

            /* Modal Styles */
            .modal-overlay {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: rgba(0,0,0,0.6);
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 1000;
              padding: 20px;
              backdrop-filter: blur(4px);
            }

            .modal-content {
              background: white;
              border-radius: 20px;
              width: 100%;
              max-width: 500px;
              max-height: 90vh;
              overflow-y: auto;
              box-shadow: 0 25px 50px rgba(0,0,0,0.25);
            }

            .modal-header {
              padding: 25px;
              border-bottom: 2px solid #f1f5f9;
              display: flex;
              align-items: center;
              justify-content: space-between;
            }

            .modal-title {
              font-size: 22px;
              font-weight: 800;
              color: #1e293b;
              margin: 0;
            }

            .close-button {
              background: #f1f5f9;
              border: none;
              width: 40px;
              height: 40px;
              border-radius: 50%;
              font-size: 24px;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              transition: all 0.3s;
            }

            .close-button:hover {
              background: #e2e8f0;
              transform: rotate(90deg);
            }

            .modal-body {
              padding: 25px;
            }

            .pour-details {
              background: #f8fafc;
              border-radius: 12px;
              padding: 20px;
              margin-bottom: 20px;
            }

            .detail-row {
              display: flex;
              justify-content: space-between;
              padding: 10px 0;
              border-bottom: 1px solid #e2e8f0;
            }

            .detail-row:last-child {
              border-bottom: none;
            }

            .detail-label {
              font-weight: 700;
              color: #64748b;
            }

            .detail-value {
              font-weight: 600;
              color: #1e293b;
            }

            .form-group {
              margin-top: 20px;
            }

            .form-label {
              display: block;
              font-size: 14px;
              font-weight: 700;
              color: #475569;
              margin-bottom: 10px;
            }

            .form-textarea {
              width: 100%;
              padding: 15px;
              border: 2px solid #e2e8f0;
              border-radius: 12px;
              font-size: 14px;
              min-height: 100px;
              resize: vertical;
              transition: all 0.3s;
              box-sizing: border-box;
            }

            .form-textarea:focus {
              outline: none;
              border-color: #f59e0b;
              box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.1);
            }

            .modal-actions {
              padding: 20px 25px;
              border-top: 2px solid #f1f5f9;
              display: flex;
              gap: 12px;
              justify-content: flex-end;
            }

            .modal-btn {
              padding: 12px 24px;
              border-radius: 10px;
              font-size: 14px;
              font-weight: 700;
              cursor: pointer;
              transition: all 0.3s;
              border: none;
            }

            .modal-btn-cancel {
              background: #f1f5f9;
              color: #64748b;
            }

            .modal-btn-cancel:hover {
              background: #e2e8f0;
            }

            .modal-btn-approve {
              background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%);
              color: white;
            }

            .modal-btn-approve:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(22, 163, 74, 0.3);
            }

            .modal-btn-reject {
              background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
              color: white;
            }

            .modal-btn-reject:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
            }

            .modal-btn:disabled {
              opacity: 0.5;
              cursor: not-allowed;
              transform: none;
            }

            /* Desktop Table */
            .desktop-table {
              display: block;
            }

            /* Mobile Cards */
            .mobile-cards {
              display: none;
            }

            .pour-card-mobile {
              background: white;
              border-radius: 16px;
              padding: 20px;
              margin-bottom: 15px;
              box-shadow: 0 4px 15px rgba(0,0,0,0.08);
              border: 2px solid #f1f5f9;
            }

            .card-header-mobile {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 15px;
              padding-bottom: 15px;
              border-bottom: 2px solid #f1f5f9;
            }

            .card-number {
              font-size: 24px;
              font-weight: 900;
              color: #f59e0b;
            }

            .card-row {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
              border-bottom: 1px solid #f8fafc;
            }

            .card-label {
              font-weight: 700;
              color: #64748b;
              font-size: 13px;
            }

            .card-value {
              font-weight: 600;
              color: #1e293b;
              font-size: 13px;
              text-align: left;
            }

            .card-actions {
              display: flex;
              gap: 8px;
              padding-top: 15px;
              margin-top: 15px;
              border-top: 2px solid #f1f5f9;
            }

            @media (max-width: 1024px) {
              .desktop-table {
                display: none;
              }

              .mobile-cards {
                display: block;
              }
            }

            @media (max-width: 768px) {
              .pours-container {
                padding: 15px;
              }

              .pours-header {
                padding: 20px;
                border-radius: 16px;
              }

              .header-title {
                font-size: 22px;
              }

              .header-icon {
                font-size: 28px;
              }

              .header-subtitle {
                font-size: 13px;
              }

              .filters-grid {
                grid-template-columns: 1fr;
                gap: 15px;
              }

              .stats-card {
                flex-direction: column;
                text-align: center;
                gap: 15px;
              }

              .stat-value {
                font-size: 36px;
              }

              .stat-icon {
                font-size: 60px;
              }

              .table-header {
                padding: 15px 20px;
              }

              .table-header-title {
                font-size: 16px;
              }
            }

            @media (max-width: 480px) {
              .header-title {
                font-size: 18px;
                flex-direction: column;
                gap: 10px;
              }

              .stat-value {
                font-size: 32px;
              }

              .pour-card-mobile {
                padding: 15px;
              }

              .card-number {
                font-size: 20px;
              }
            }
          `}</style>

          <div className="pours-container">
            {/* Header & Filters */}
            <div className="pours-header">
              <div className="header-content">
                <h2 className="header-title">
                  <span className="header-icon">⏳</span>
                  الصبات في انتظار المراجعة
                </h2>
                <p className="header-subtitle">
                  جميع الصبات التي في انتظار المراجعة والموافقة من الإدارة
                </p>
                
                <div className="filters-grid">
                  <div className="filter-group">
                    <label className="filter-label">
                      <span className="filter-icon">📍</span>
                      المحطة
                    </label>
                    <select
                      className="filter-select"
                      value={filters.station}
                      onChange={(value) => setFilters({...filters, station: value})}
                    >
                      <option value="all">كل المحطات</option>
                      <option value="ST13 Giza">ST13 Giza</option>
                      <option value="ST14 Giza Square">ST14 Giza Square</option>
                      <option value="S15 Alrawda">S15 Alrawda</option>
                      <option value="ST16 Almalek Elsaleh">ST16 Almalek Elsaleh</option>
                      <option value="ST17 Cut&Cover">ST17 Cut&Cover</option>
                      <option value="Abo Rawash">Abo Rawash</option>
                      <option value="4TL">4TL</option>
                      <option value="Unimix">Unimix</option>
                      <option value="Green">Green</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">
                      <span className="filter-icon">🔬</span>
                      نوع الاختبار
                    </label>
                    <select
                      className="filter-select"
                      value={filters.testType}
                      onChange={(value) => setFilters({...filters, testType: value})}
                    >
                      <option value="all">كل الأنواع</option>
                      <option value="Concrete Pour">Concrete Pour</option>
                      <option value="Grout">Grout</option>
                      <option value="T.M Trail">T.M Trail</option>
                      <option value="Chemical Test">Chemical Test</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">
                      <span className="filter-icon">📅</span>
                      From Date
                    </label>
                    <div className="date-picker-wrapper">
                      <DatePicker value={filters.dateFrom}
                        onChange={(value) => setFilters({...filters, dateFrom: value})}
                        isClearable
                        showMonthDropdown
                        showYearDropdown
                        max={filters.dateTo || new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">
                      <span className="filter-icon">📅</span>
                      To Date
                    </label>
                    <div className="date-picker-wrapper">
                      <DatePicker value={filters.dateTo}
                        onChange={(value) => setFilters({...filters, dateTo: value})}
                        isClearable
                        showMonthDropdown
                        showYearDropdown
                        min={filters.dateFrom}
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Card */}
            <div className="stats-card">
              <div className="stat-content">
                <p className="stat-value">{pours.length}</p>
                <p className="stat-label">Pending Pour</p>
              </div>
              <span className="stat-icon">📋</span>
            </div>

            {/* Table */}
            <div className="table-card">
              <div className="table-header">
                <h3 className="table-header-title">
                  <span>📝</span>
                  Pending Pours List
                </h3>
              </div>

              {loading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p className="loading-text">Loading...</p>
                </div>
              ) : pours.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">✅</div>
                  <h3 className="empty-title">No Pending Pours</h3>
                  <p className="empty-text">All pours have been reviewed</p>
                </div>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <div className="desktop-table">
                    <div className="table-wrapper">
                      <table className="pours-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Status</th>
                            <th>Type</th>
                            <th>Date</th>
                            <th>Station</th>
                            <th>Element</th>
                            <th>Mix Code</th>
                            <th>Quantity</th>
                            <th>Cubes</th>
                            <th>CH</th>
                            <th>Plant</th>
                            <th>Start</th>
                            <th>Finish</th>
                            <th>Created By</th>
                            <th>Created At</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pours.map((pour, index) => (
                            <tr key={pour._id}>
                              <td>{index + 1}</td>
                              <td>
                                <span className="status-badge">
                                  <span>⏳</span>
                                  Pending
                                </span>
                              </td>
                              <td>
                                <span className={`test-type-badge ${
                                  pour.testType === 'Concrete Pour' ? 'test-type-concrete' :
                                  pour.testType === 'Grout' ? 'test-type-grout' : 'test-type-trail'
                                }`}>
                                  {pour.testType || 'Concrete Pour'}
                                </span>
                              </td>
                              <td>{formatDate(pour.castingDate)}</td>
                              <td>{getStationName(pour)}</td>
                              <td>{pour.location}</td>
                              <td>{pour.mix}</td>
                              <td>{pour.quantity} m³</td>
                              <td>{calculateCubes(pour.cubesCount)}</td>
                              <td>{pour.chemistry || '-'}</td>
                              <td>{pour.batchPlant}</td>
                              <td>{pour.startTime || '-'}</td>
                              <td>{pour.finishTime || '-'}</td>
                              <td>{pour.createdBy?.fullName || '-'} ({pour.createdBy?.role || ''})</td>
                              <td>{formatDateTime(pour.createdAt)}</td>
                              <td>
                                <div className="action-buttons">
                                  <button
                                    className="btn-approve"
                                    onClick={() => openApproveModal(pour)}
                                    disabled={loading}
                                  >
                                    <span>✓</span>
                                    Approve
                                  </button>
                                  <button
                                    className="btn-reject"
                                    onClick={() => openRejectModal(pour)}
                                    disabled={loading}
                                  >
                                    <span>✕</span>
                                    Reject
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile Cards View */}
                  <div className="mobile-cards">
                    {pours.map((pour, index) => (
                      <div key={pour._id} className="pour-card-mobile">
                        <div className="card-header-mobile">
                          <span className="card-number">#{index + 1}</span>
                          <span className="status-badge">
                            <span>⏳</span>
                            Pending
                          </span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Type:</span>
                          <span className="card-value">
                            <span className={`test-type-badge ${
                              pour.testType === 'Concrete Pour' ? 'test-type-concrete' :
                              pour.testType === 'Grout' ? 'test-type-grout' : 'test-type-trail'
                            }`}>
                              {pour.testType || 'Concrete Pour'}
                            </span>
                          </span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Date:</span>
                          <span className="card-value">{formatDate(pour.castingDate)}</span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Station:</span>
                          <span className="card-value">{getStationName(pour)}</span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Element:</span>
                          <span className="card-value">{pour.location}</span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Mix Code:</span>
                          <span className="card-value">{pour.mix}</span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Quantity:</span>
                          <span className="card-value">{pour.quantity} m³</span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Cubes:</span>
                          <span className="card-value">{calculateCubes(pour.cubesCount)}</span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">CH:</span>
                          <span className="card-value">{pour.chemistry || '-'}</span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Plant:</span>
                          <span className="card-value">{pour.batchPlant}</span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Start:</span>
                          <span className="card-value">{pour.startTime || '-'}</span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Finish:</span>
                          <span className="card-value">{pour.finishTime || '-'}</span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Created By:</span>
                          <span className="card-value">
                            {pour.createdBy?.fullName || '-'} ({pour.createdBy?.role || ''})
                          </span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Created At:</span>
                          <span className="card-value">{formatDateTime(pour.createdAt)}</span>
                        </div>

                        <div className="card-actions">
                          <button
                            className="btn-approve"
                            onClick={() => openApproveModal(pour)}
                            disabled={loading}
                            style={{flex: 1}}
                          >
                            <span>✓</span>
                            Approve
                          </button>
                          <button
                            className="btn-reject"
                            onClick={() => openRejectModal(pour)}
                            disabled={loading}
                            style={{flex: 1}}
                          >
                            <span>✕</span>
                            Reject
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Modal */}
          {showModal && selectedPour && (
            <div className="modal-overlay" onClick={closeModal}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3 className="modal-title">
                    {modalType === 'approve' ? '✅ Confirm Approval' : '❌ Reject Pour'}
                  </h3>
                  <button className="close-button" onClick={closeModal}>×</button>
                </div>

                <div className="modal-body">
                  <div className="pour-details">
                    <div className="detail-row">
                      <span className="detail-label">Station:</span>
                      <span className="detail-value">{getStationName(selectedPour)}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Date:</span>
                      <span className="detail-value">{formatDate(selectedPour.castingDate)}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Element:</span>
                      <span className="detail-value">{selectedPour.location}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Quantity:</span>
                      <span className="detail-value">{selectedPour.quantity} m³</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Created By:</span>
                      <span className="detail-value">
                        {selectedPour.createdBy?.fullName} ({selectedPour.createdBy?.role})
                      </span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {modalType === 'approve' ? 'Notes (Optional):' : 'Rejection Reason (Required): *'}
                    </label>
                    <textarea
                      className="form-textarea"
                      value={notes}
                      onChange={(value) => setNotes(value)}
                      placeholder={
                        modalType === 'approve' 
                          ? 'Add notes if any...' 
                          : 'Write rejection reason...'
                      }
                      required={modalType === 'reject'}
                    />
                  </div>
                </div>

                <div className="modal-actions">
                  <button
                    className="modal-btn modal-btn-cancel"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  {modalType === 'approve' ? (
                    <button
                      className="modal-btn modal-btn-approve"
                      onClick={handleApprove}
                      disabled={loading}
                    >
                      {loading ? 'Approving...' : '✅ Approve'}
                    </button>
                  ) : (
                    <button
                      className="modal-btn modal-btn-reject"
                      onClick={handleReject}
                      disabled={loading}
                    >
                      {loading ? 'Rejecting...' : '❌ Reject'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Pours;